import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { from, switchMap } from 'rxjs';

/**
 * HTTP Interceptor that automatically adds the Keycloak bearer token to all requests
 * 
 * This interceptor:
 * - Gets the current Keycloak token using getToken() to ensure it's available
 * - Updates token if needed (refreshes if expires in 30 seconds)
 * - Adds it as an Authorization header (Bearer token)
 * - Skips if no token is available (user not logged in)
 * 
 * @example
 * // In app.config.ts:
 * provideHttpClient(withInterceptors([authTokenInterceptor]))
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(KeycloakService);

  // Skip interceptor for Keycloak auth URLs (login, logout, token, revoke) to prevent infinite loops
  if (
    req.url.includes('keycloak') ||
    req.url.includes('/auth/realms/') ||
    req.url.includes('/protocol/openid-connect/revoke')
  ) {
    return next(req);
  }

  // Skip interceptor for S3/AWS presigned URLs (they have auth in URL params)
  if (req.url.includes('.s3.') || req.url.includes('.amazonaws.com') || req.url.includes('minio.iudx.io') || req.url.includes('storage.googleapis.com')) {
    console.log('Auth token interceptor: Skipping S3/presigned URL', { url: req.url });
    return next(req);
  }

  // Skip interceptor for public challenge rules download (returns S3 URL for PDF viewer; no auth required)
  if (req.url.includes('/challenge/attachment/download/rules_and_guidelines/')) {
    return next(req);
  }

  // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
  const isFormData = req.body instanceof FormData;
  // Reaction endpoint requires Content-Type: text/plain; do not override
  const isReactionEndpoint = req.url.includes('/reaction');
  // Never force JSON Content-Type on GET/HEAD/DELETE: breaks some gateways for file downloads and is invalid for bodyless GETs.
  const method = (req.method || 'GET').toUpperCase();
  const shouldSetJsonContentType =
    !req.headers.has('Content-Type') &&
    !isFormData &&
    !isReactionEndpoint &&
    (method === 'POST' || method === 'PUT' || method === 'PATCH');

  // Use RxJS to handle async token retrieval
  return from(
    (async () => {
      try {
        // Check if user is logged in
        const isLoggedIn = await keycloak.isLoggedIn();
        if (!isLoggedIn) {
          console.warn('Auth token interceptor: User not logged in', { url: req.url });
          return null;
        }

        // Update token if needed (refresh if expires in 30 seconds)
        await keycloak.updateToken(30);

        // Get the current token - retry once if not ready (fixes race on first load after nav)
        let token = await keycloak.getToken();
        if ((!token || !token.trim()) && isLoggedIn) {
          await new Promise((r) => setTimeout(r, 400));
          await keycloak.updateToken(30);
          token = await keycloak.getToken();
        }
        return token;
      } catch (error) {
        console.error('Auth token interceptor: Error getting token', {
          url: req.url,
          error: error
        });
        return null;
      }
    })()
  ).pipe(
    switchMap((token) => {
      // Prepare headers
      const headers: { [key: string]: string } = {};

      if (shouldSetJsonContentType) {
        headers['Content-Type'] = 'application/json';
      }

      // Add Authorization header if token is available
      if (token && token.trim() !== '') {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('Auth token interceptor: Token added to request', {
          url: req.url,
          hasToken: !!token,
          tokenLength: token.length,
          tokenPreview: token.substring(0, 20) + '...'
        });
      } else {
        console.warn('Auth token interceptor: No token available for request', {
          url: req.url
        });
      }

      // Clone the request and add headers
      const authReq = req.clone({
        setHeaders: headers,
      });
      return next(authReq);
    })
  );
};




